import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, MapPin, CheckCircle2, X, Eye, DollarSign, Repeat } from "lucide-react";

const notifIcon = (t) => {
  if (t === "checkin") return <MapPin className="w-4 h-4 text-[var(--accent)]" />;
  if (t === "invoice_viewed") return <Eye className="w-4 h-4 text-[#1d4ed8]" />;
  if (t === "invoice_paid") return <DollarSign className="w-4 h-4 text-[#0d7d72]" />;
  if (t === "invoice_reminder") return <Bell className="w-4 h-4 text-[#946200]" />;
  if (t === "invoice_recurring") return <Repeat className="w-4 h-4 text-[var(--accent)]" />;
  return <CheckCircle2 className="w-4 h-4 text-[#0d7d72]" />;
};
import api from "@/lib/api";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const lastTopRef = useRef(null);
  const seededRef = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
      const top = data.items?.[0];
      if (seededRef.current && top && top.id !== lastTopRef.current && !top.read) {
        toast(top.title, { description: top.detail, icon: top.type === "checkin" ? "📍" : "✅" });
      }
      if (top) lastTopRef.current = top.id;
      seededRef.current = true;
    } catch (e) {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setUnread(0); setItems((xs) => xs.map((x) => ({ ...x, read: true }))); } catch (e) {}
  };

  const toggle = () => { const n = !open; setOpen(n); if (n && unread > 0) markAll(); };

  return (
    <div className="relative">
      <button data-testid="notif-bell" onClick={toggle} className="relative flex items-center justify-center w-9 h-9 border border-[var(--line)] bg-white hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full bg-[#FF6B6B] text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white border border-[var(--line)] shadow-xl z-50">
            <div className="sticky top-0 flex items-center justify-between px-4 h-12 border-b border-[var(--line)] bg-white">
              <span className="label-mono text-[var(--text-2)]">Crew activity</span>
              <button onClick={() => setOpen(false)} className="text-[var(--text-2)] hover:text-[var(--accent)]"><X className="w-4 h-4" /></button>
            </div>
            {items.length === 0 ? (
              <p className="p-6 text-sm text-[var(--text-2)] text-center">No activity yet. You'll see crew task completions and check-ins here.</p>
            ) : (
              items.map((n) => (
                <div key={n.id} data-testid={`notif-item-${n.id}`} className={`flex items-start gap-3 px-4 py-3 border-b border-[var(--line)] ${n.read ? "" : "bg-[var(--accent)]/5"}`}>
                  <div className="mt-0.5 shrink-0">{notifIcon(n.type)}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-snug">{n.title}</p>
                    <p className="text-xs text-[var(--text-2)] mt-0.5 truncate">{n.detail}</p>
                    <p className="text-[11px] text-[var(--text-2)] font-mono mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  {n.maps_url && <a href={n.maps_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--accent)] hover:underline shrink-0">Map</a>}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
